import { useState, useEffect, useRef } from 'react'
import { useGetSwapQuoteQuery } from '../../../../../generated/graphql'

interface UseQuoteProps {
  fromMintId: string
  toMintId: string
  fromAmount: string
  selectedCompanyId: string
  isAmountGreaterThanBalance: boolean
}

const FAILURE_RETRY_MIN_DELAY_MS = 1_000

export function useQuote({
  fromMintId,
  toMintId,
  fromAmount,
  selectedCompanyId,
  isAmountGreaterThanBalance,
}: UseQuoteProps) {
  const [quoteCountdown, setQuoteCountdown] = useState(15)
  const [currentQuoteId, setCurrentQuoteId] = useState<string>('')
  const [failureRetryCycle, setFailureRetryCycle] = useState(0)
  const quoteIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const wasQuoteFetchingRef = useRef(false)

  const shouldFetchQuote = Boolean(
    fromMintId &&
      toMintId &&
      fromAmount &&
      parseFloat(fromAmount) > 0 &&
      !isAmountGreaterThanBalance,
  )

  const [{ data: quoteData, fetching: quoteFetching }, fetchQuote] = useGetSwapQuoteQuery({
    variables: {
      input: {
        fromMintId,
        toMintId,
        fromAmount,
        companyId: selectedCompanyId,
      },
    },
    pause: !shouldFetchQuote,
    requestPolicy: 'network-only',
  })

  const quoteResult = quoteData?.getSwapQuote
  const currentQuote =
    quoteResult?.status === 'SUCCESS' &&
    quoteResult.toAmount != null &&
    quoteResult.exchangeRate != null &&
    quoteResult.quoteId != null &&
    quoteResult.expiresAt != null
      ? {
          toAmount: quoteResult.toAmount,
          exchangeRate: quoteResult.exchangeRate,
          quoteId: quoteResult.quoteId,
          expiresAt: quoteResult.expiresAt,
        }
      : null
  const failureRetryAt = quoteResult?.status === 'FAILED' ? quoteResult.retryAt : null

  useEffect(() => {
    if (wasQuoteFetchingRef.current && !quoteFetching && failureRetryAt) {
      setFailureRetryCycle((cycle) => cycle + 1)
    }
    wasQuoteFetchingRef.current = quoteFetching
  }, [quoteFetching, failureRetryAt])

  // Quote management
  useEffect(() => {
    if (currentQuote?.quoteId && currentQuote.quoteId !== currentQuoteId) {
      setCurrentQuoteId(currentQuote.quoteId)
    }
  }, [currentQuote?.quoteId, currentQuoteId])

  useEffect(() => {
    if (currentQuote?.expiresAt) {
      const timer = setInterval(() => {
        setQuoteCountdown((prev) => {
          if (prev === 0) {
            fetchQuote({ requestPolicy: 'network-only' })
            return 15
          }
          return prev - 1
        })
      }, 1000)
      return () => clearInterval(timer)
    }
  }, [currentQuote?.expiresAt, fetchQuote])

  useEffect(() => {
    if (quoteIntervalRef.current) {
      clearInterval(quoteIntervalRef.current)
      quoteIntervalRef.current = null
    }

    if (shouldFetchQuote && !currentQuote && !quoteFetching && !failureRetryAt) {
      quoteIntervalRef.current = setInterval(() => {
        fetchQuote({ requestPolicy: 'network-only' })
      }, 1000)
    }

    return () => {
      if (quoteIntervalRef.current) {
        clearInterval(quoteIntervalRef.current)
        quoteIntervalRef.current = null
      }
    }
  }, [shouldFetchQuote, currentQuote, quoteFetching, failureRetryAt, fetchQuote])

  useEffect(() => {
    if (!shouldFetchQuote || !failureRetryAt) return
    const timeout = setTimeout(
      () => fetchQuote({ requestPolicy: 'network-only' }),
      Math.max(FAILURE_RETRY_MIN_DELAY_MS, new Date(failureRetryAt).getTime() - Date.now()),
    )
    return () => clearTimeout(timeout)
  }, [shouldFetchQuote, failureRetryAt, failureRetryCycle, fetchQuote])

  return {
    currentQuote,
    quoteCountdown,
    quoteFetching,
    quoteStatus: quoteResult?.status,
    failureRetryAt,
    shouldFetchQuote,
  }
}
